import type { CareStateDomainDb, LinkT, OrphanLinkT } from '../model/types';

function orphanLink(): OrphanLinkT {
  return { type: 'orphan' } as const;
}

function hasProblem(db: CareStateDomainDb, problemId: string): boolean {
  return db.problems.some((p) => p.id === problemId);
}

function hasRequest(db: CareStateDomainDb, requestId: string): boolean {
  return db.requests.some((r) => r.id === requestId);
}

/**
 * Compute a resource's "actual link" for visibility decisions.
 *
 * Rules:
 * - If link is RequestLinkT but the linked request doesn't exist => OrphanLinkT
 * - If link is ProblemLinkT but the linked problem doesn't exist => OrphanLinkT
 * - PatientLinkT and OrphanLinkT remain unchanged
 * If `type` is missing/unknown, we do not guess; we return the link unchanged.
 */
export function actualLink(link: LinkT, db: CareStateDomainDb): LinkT {
  switch (link.type) {
    case 'patient':
    case 'orphan':
      return link;
    case 'problem':
      return hasProblem(db, link.id) ? link : orphanLink();
    case 'request':
      return hasRequest(db, link.id) ? link : orphanLink();
    case undefined:
      // Avoid guessing when discriminator isn't present.
      return link;
    default: {
      const _exhaustive: never = link;
      return _exhaustive;
    }
  }
}
